import type { Direction } from '../sim/direction';
import type { Tile } from '../sim/grid';
import type { SimLevel } from '../sim/level';
import {
  BREAK_TIER,
  MEADOW_REGROW_TILES,
  meadowPatches,
  TILE_BY_ID,
  TILES,
  type TileDef,
  type TileId,
} from './tiles';

/**
 * Compact text format for hand-built maps, baked levels and tests. Each character is a tile's
 * `char` from the catalog (src/content/tiles.ts), plus `S` = start position on grass:
 *
 *   .  grass        ,  tall grass    _  path         *  flower bed    ~  mud
 *   #  rock         B  bird bath     T  tree         h  shrub         g  gnome
 *   x  sticks       w  pond          H  house        z  garden hose   K  dog house
 *   q  sprinkler    J  judges' table (lawn art)   S  start (grass)
 *   Park: s sandpit, b bench, l lamp post, n trash bin
 *   Farm: R barn, f fence, v wheat, y hay bale, c vegetable patch, e hedgerow, u water trough
 */
const LEGEND: ReadonlyMap<string, TileDef> = new Map([
  ...TILES.map((t) => [t.char, t] as const),
  ['S', TILE_BY_ID.grass] as const,
]);

const CHAR_BY_ID: Readonly<Record<TileId, string>> = Object.fromEntries(
  TILES.map((t) => [t.id, t.char]),
) as Record<TileId, string>;

export interface AsciiMap {
  width: number;
  height: number;
  tiles: Tile[];
  art: TileId[];
  /** Break tier per tile (-1: not breakable), from {@link BREAK_TIER}. */
  toughness: number[];
  /** Position of the `S` marker, if present. */
  start: { x: number; y: number } | null;
}

export function parseAsciiMap(rows: readonly string[]): AsciiMap {
  if (rows.length === 0) throw new RangeError('parseAsciiMap: no rows');
  const width = rows[0]?.length ?? 0;
  if (width === 0) throw new RangeError('parseAsciiMap: empty first row');

  const tiles: Tile[] = [];
  const art: TileId[] = [];
  const toughness: number[] = [];
  let start: AsciiMap['start'] = null;
  rows.forEach((row, y) => {
    if (row.length !== width) {
      throw new RangeError(`parseAsciiMap: row ${y} has length ${row.length}, expected ${width}`);
    }
    [...row].forEach((ch, x) => {
      const def = LEGEND.get(ch);
      if (!def) throw new RangeError(`parseAsciiMap: unknown '${ch}' at ${x},${y}`);
      if (ch === 'S') {
        if (start) throw new RangeError('parseAsciiMap: more than one start marker');
        start = { x, y };
      }
      tiles.push(def.kind);
      art.push(def.id);
      toughness.push(BREAK_TIER[def.id] ?? -1);
    });
  });
  return { width, height: rows.length, tiles, art, toughness, start };
}

/** Inverse of {@link parseAsciiMap}: art ids back to rows (start marked with `S`). */
export function toAsciiRows(
  width: number,
  art: readonly TileId[],
  start?: { x: number; y: number },
): string[] {
  const rows: string[] = [];
  for (let i = 0; i < art.length; i += width) {
    rows.push(
      art
        .slice(i, i + width)
        .map((id) => CHAR_BY_ID[id])
        .join(''),
    );
  }
  if (start) {
    const row = rows[start.y] ?? '';
    rows[start.y] = row.slice(0, start.x) + 'S' + row.slice(start.x + 1);
  }
  return rows;
}

/** The regrowing meadow of a map, if it has any meadow grass. */
export function regrowFor(
  art: readonly TileId[],
  width: number,
  delayTiles = MEADOW_REGROW_TILES,
): Pick<SimLevel, 'regrow'> {
  const patches = meadowPatches(art, width);
  return patches.length > 0 ? { regrow: { patches, delayTiles } } : {};
}

/** Builds a complete sim level from ASCII rows (start from the `S` marker, else top-left). */
export function levelFromAscii(
  rows: readonly string[],
  dir: Direction,
  overrides: Partial<SimLevel> = {},
): SimLevel {
  const map = parseAsciiMap(rows);
  return {
    id: overrides.id ?? 'ascii',
    seed: overrides.seed ?? overrides.id ?? 'ascii',
    width: map.width,
    height: map.height,
    tiles: map.tiles,
    toughness: map.toughness,
    start: { ...(map.start ?? { x: 0, y: 0 }), dir },
    ...regrowFor(map.art, map.width),
    ...overrides,
  };
}
